// src/components/RendicontazionePage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import categories from "../data/categories";
import { API_BASE } from "../config";
import "./RendicontazionePage.css";

/* ---------- Helpers ---------- */

function parseAmount(v) {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;

  const s = String(v).trim();
  if (!s) return 0;

  const hasDot = s.includes(".");
  const hasComma = s.includes(",");

  // formato europeo "1.234,56"
  if (hasDot && hasComma) {
    const norm = s.replace(/\./g, "").replace(",", ".");
    const n = Number(norm);
    return Number.isFinite(n) ? n : 0;
  }

  // solo virgola "1234,56"
  if (hasComma && !hasDot) {
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }

  // default "1234.56"
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function safeDateToMs(v) {
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

async function readErrorMessage(res) {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    const data = await res.json().catch(() => null);
    return data?.message || data?.error || `Errore HTTP ${res.status}`;
  }
  const text = await res.text().catch(() => "");
  return text || `Errore HTTP ${res.status}`;
}

const eurFmt = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });
function fmtEUR(value) {
  const n = Number(value || 0);
  return eurFmt.format(Number.isFinite(n) ? n : 0);
}

function toDateLabelIt(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
}

/* ---------- Page ---------- */

export default function RendicontazionePage({ token, user }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // accordion open states (per colore)
  const [openColors, setOpenColors] = useState(() => new Set());

  // ✅ Drill-down: selezione categoria -> modal lista movimenti (per anno)
  const [detail, setDetail] = useState(null); // { year, color, categoryName, categoryLabel, tx: [] }
  const [q, setQ] = useState(""); // filtro dentro modal (descrizione / fornitore / note)
  const [onlyUscite, setOnlyUscite] = useState(false);
  const [onlyEntrate, setOnlyEntrate] = useState(false);

  // mappa categoria -> meta
  const categoryMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(categories) ? categories : []).forEach((c) => {
      map.set(String(c.name), {
        color: c.color || "#999999",
        label: c.label || c.name,
        name: c.name,
        type: c.type,
      });
    });
    return map;
  }, []);

  const getCategoryMeta = useCallback(
    (categoryName) => {
      const key = String(categoryName || "");
      const m = categoryMap.get(key);
      if (m) return m;
      return { color: "#999999", label: "Senza categoria", name: key || "" };
    },
    [categoryMap]
  );

  const fetchTransactions = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError("");

    const controller = new AbortController();

    try {
      const res = await fetch(`${API_BASE}/api/transactions`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(await readErrorMessage(res));
      const data = await res.json().catch(() => []);
      setTransactions(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setTransactions([]);
      setError(e?.message || "Errore nel recupero delle transazioni");
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cleanup = null;

    (async () => {
      cleanup = await fetchTransactions();
    })();

    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [token, fetchTransactions]);

  // anni disponibili
  const availableYears = useMemo(() => {
    const years = new Set();
    for (const t of transactions) {
      const ms = safeDateToMs(t?.data);
      if (!ms) continue;
      years.add(new Date(ms).getFullYear());
    }
    if (years.size === 0) {
      const now = new Date().getFullYear();
      return [now, now - 1, now - 2, now - 3];
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [transactions]);

  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());

  useEffect(() => {
    if (!availableYears.length) return;
    if (!availableYears.includes(selectedYear)) setSelectedYear(availableYears[0]);
  }, [availableYears, selectedYear]);

  const txForYear = useMemo(() => {
    return (Array.isArray(transactions) ? transactions : [])
      .filter((t) => new Date(t?.data).getFullYear() === selectedYear)
      .slice()
      .sort((a, b) => safeDateToMs(b?.data) - safeDateToMs(a?.data));
  }, [transactions, selectedYear]);

  // Totali anno (kpi mini)
  const yearTotals = useMemo(() => {
    let entrate = 0;
    let uscite = 0;
    for (const t of txForYear) {
      const imp = parseAmount(t?.importo);
      if (imp >= 0) entrate += imp;
      else uscite += Math.abs(imp);
    }
    return { entrate, uscite, saldo: entrate - uscite };
  }, [txForYear]);

  /**
   * Struttura:
   * groups[color] = {
   *   color, label,
   *   totals: { entrate, uscite, saldo },
   *   categories: [
   *     { name, label, entrate, uscite, saldo, count }
   *   ]
   * }
   */
  const groupsByColor = useMemo(() => {
    const groups = new Map();

    // prepopola da categories => ogni colore avrà sempre le sue categorie
    (Array.isArray(categories) ? categories : []).forEach((cat) => {
      const color = cat.color || "#999999";
      const label = cat.label || cat.name;

      if (!groups.has(color)) {
        groups.set(color, {
          color,
          label,
          totals: { entrate: 0, uscite: 0, saldo: 0, count: 0 },
          categories: new Map(), // key: categoryName
        });
      }

      const g = groups.get(color);
      if (!g.categories.has(cat.name)) {
        g.categories.set(cat.name, {
          name: cat.name,
          label: cat.label || cat.name,
          entrate: 0,
          uscite: 0,
          saldo: 0,
          count: 0,
        });
      }
    });

    // riempi da transazioni
    for (const t of txForYear) {
      const meta = getCategoryMeta(t?.categoria);
      const color = meta.color || "#999999";

      if (!groups.has(color)) {
        groups.set(color, {
          color,
          label: meta.label || "Senza categoria",
          totals: { entrate: 0, uscite: 0, saldo: 0, count: 0 },
          categories: new Map(),
        });
      }

      const g = groups.get(color);

      const catName = String(t?.categoria || "Senza categoria");
      if (!g.categories.has(catName)) {
        g.categories.set(catName, {
          name: catName,
          label: meta.label || catName,
          entrate: 0,
          uscite: 0,
          saldo: 0,
          count: 0,
        });
      }

      const row = g.categories.get(catName);

      const imp = parseAmount(t?.importo);
      if (imp >= 0) row.entrate += imp;
      else row.uscite += Math.abs(imp);
      row.count += 1;
      row.saldo = row.entrate - row.uscite;
    }

    // calcola totali per colore
    for (const [, g] of groups) {
      let e = 0;
      let u = 0;
      let c = 0;
      for (const [, r] of g.categories) {
        e += parseAmount(r.entrate);
        u += parseAmount(r.uscite);
        c += Number(r.count || 0);
      }
      g.totals.entrate = e;
      g.totals.uscite = u;
      g.totals.saldo = e - u;
      g.totals.count = c;
    }

    // converti categories map -> array + sort
    const out = Array.from(groups.values()).map((g) => {
      const cats = Array.from(g.categories.values()).sort((a, b) =>
        String(a.label || "").localeCompare(String(b.label || ""), "it")
      );
      return { ...g, categories: cats };
    });

    // sort gruppi per label
    out.sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), "it"));
    return out;
  }, [txForYear, getCategoryMeta]);

  // open all / close all
  const openAll = useCallback(() => {
    setOpenColors(new Set(groupsByColor.map((g) => g.color)));
  }, [groupsByColor]);

  const closeAll = useCallback(() => {
    setOpenColors(new Set());
  }, []);

  const toggleColor = useCallback((color) => {
    setOpenColors((prev) => {
      const next = new Set(prev);
      if (next.has(color)) next.delete(color);
      else next.add(color);
      return next;
    });
  }, []);

  // ✅ apri dettaglio categoria (modal con elenco movimenti)
  const openCategoryDetail = useCallback(
    (color, cat) => {
      const catName = String(cat?.name || "");
      const catLabel = String(cat?.label || catName);

      const tx = txForYear
        .filter((t) => String(t?.categoria || "Senza categoria") === catName)
        .slice()
        .sort((a, b) => safeDateToMs(b?.data) - safeDateToMs(a?.data));

      setQ("");
      setOnlyEntrate(false);
      setOnlyUscite(false);
      setDetail({ year: selectedYear, color, categoryName: catName, categoryLabel: catLabel, tx });
    },
    [txForYear, selectedYear]
  );

  const closeDetail = useCallback(() => setDetail(null), []);

  useEffect(() => {
    if (!detail) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeDetail();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail, closeDetail]);

  const filteredDetailTx = useMemo(() => {
    if (!detail) return [];
    const needle = String(q || "").trim().toLowerCase();

    return (detail.tx || []).filter((t) => {
      const imp = parseAmount(t?.importo);
      if (onlyUscite && !(imp < 0)) return false;
      if (onlyEntrate && !(imp >= 0)) return false;

      if (!needle) return true;

      const hay = [
        t?.descrizione,
        t?.fornitore,
        t?.note,
        t?.tipo,
        t?.categoria,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(needle);
    });
  }, [detail, q, onlyUscite, onlyEntrate]);

  const detailTotals = useMemo(() => {
    if (!detail) return { entrate: 0, uscite: 0, saldo: 0 };
    let e = 0;
    let u = 0;
    for (const t of filteredDetailTx) {
      const imp = parseAmount(t?.importo);
      if (imp >= 0) e += imp;
      else u += Math.abs(imp);
    }
    return { entrate: e, uscite: u, saldo: e - u };
  }, [detail, filteredDetailTx]);

  const pageTitle = useMemo(() => {
    const tenant = user?.tenantId ? `Tenant: ${user.tenantId}` : "Tenant: —";
    return tenant;
  }, [user]);

  if (!token) {
    return (
      <div className="rcPage">
        <div className="rcAlert rcAlert--info">Per visualizzare la rendicontazione effettua l’accesso.</div>
      </div>
    );
  }

  return (
    <div className="rcPage">
      {/* HERO */}
      <div className="rcHero">
        <div className="rcHero__inner">
          <div>
            <div className="rcKicker">Contabilità</div>
            <h2 className="rcTitle">Rendicontazione 📄</h2>
            <div className="rcSubtitle">{pageTitle}</div>
            <div className="rcSubtitle" style={{ marginTop: 8 }}>
              💰 Entrate: <b>{fmtEUR(yearTotals.entrate)}</b> · 💸 Uscite: <b>{fmtEUR(yearTotals.uscite)}</b> · 📈 Saldo:{" "}
              <b>{fmtEUR(yearTotals.saldo)}</b>
            </div>
          </div>

          <div className="rcActions">
            <div className="rcYear">
              <label htmlFor="rc-year">Anno</label>
              <select id="rc-year" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <button className="rcBtn rcBtn--ghost" type="button" onClick={fetchTransactions} disabled={loading}>
              {loading ? "⏳ Aggiorno…" : "🔄 Ricarica"}
            </button>

            <button className="rcBtn" type="button" onClick={openAll} disabled={groupsByColor.length === 0}>
              📂 Apri tutto
            </button>

            <button className="rcBtn" type="button" onClick={closeAll} disabled={groupsByColor.length === 0}>
              🧹 Chiudi tutto
            </button>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="rcContainer">
        {error && <div className="rcAlert rcAlert--error">{error}</div>}

        {loading ? (
          <div className="rcEmpty">
            <div className="rcEmpty__title">Caricamento…</div>
            <div className="rcEmpty__text">Sto recuperando i dati.</div>
          </div>
        ) : groupsByColor.length === 0 ? (
          <div className="rcEmpty">
            <div className="rcEmpty__title">Nessun dato</div>
            <div className="rcEmpty__text">Non ci sono transazioni per l’anno selezionato.</div>
          </div>
        ) : (
          <div className="rcList">
            {groupsByColor.map((g) => {
              const opened = openColors.has(g.color);
              const saldoNeg = (g.totals?.saldo || 0) < 0;

              return (
                <div key={g.color} className="rcGroup">
                  <button
                    className={`rcGroupHead ${opened ? "is-open" : ""}`}
                    type="button"
                    onClick={() => toggleColor(g.color)}
                    aria-expanded={opened}
                  >
                    <div className="rcGroupLeft">
                      <span className="rcDot" style={{ backgroundColor: g.color }} aria-hidden />
                      <div className="rcGroupTitles">
                        <div className="rcGroupTitle">{g.label}</div>
                        <div className="rcGroupSub">
                          🧾 {g.totals?.count || 0} movimenti · 🗂️ {g.categories.length} categorie
                        </div>
                      </div>
                    </div>

                    <div className="rcGroupTotals">
                      <div className="rcTot">
                        <div className="rcTotLabel">Entrate</div>
                        <div className="rcTotVal">{fmtEUR(g.totals.entrate)}</div>
                      </div>
                      <div className="rcTot">
                        <div className="rcTotLabel">Uscite</div>
                        <div className="rcTotVal">{fmtEUR(g.totals.uscite)}</div>
                      </div>
                      <div className={`rcTot ${saldoNeg ? "is-neg" : "is-pos"}`}>
                        <div className="rcTotLabel">Saldo</div>
                        <div className="rcTotVal">{fmtEUR(g.totals.saldo)}</div>
                      </div>

                      <span className="rcChevron" aria-hidden>
                        {opened ? "▴" : "▾"}
                      </span>
                    </div>
                  </button>

                  {opened && (
                    <div className="rcTableWrap" role="region" aria-label={`Dettagli ${g.label}`}>
                      <table className="rcTable">
                        <thead>
                          <tr>
                            <th>Categoria</th>
                            <th className="t-right">Entrate</th>
                            <th className="t-right">Uscite</th>
                            <th className="t-right">Saldo</th>
                            <th className="t-right" style={{ width: 120 }}>Dettagli</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.categories.map((c) => {
                            const saldo = parseAmount(c.entrate) - parseAmount(c.uscite);
                            const neg = saldo < 0;
                            const isZero = parseAmount(c.entrate) === 0 && parseAmount(c.uscite) === 0;

                            return (
                              <tr key={c.name} className={isZero ? "is-zero" : ""}>
                                <td className="rcCat">
                                  <div className="rcCatName">{c.label}</div>
                                  <div className="rcCatMeta">
                                    {c.name} · 🧾 {c.count || 0} movimenti
                                  </div>
                                </td>
                                <td className="t-right">{fmtEUR(c.entrate)}</td>
                                <td className="t-right">{fmtEUR(c.uscite)}</td>
                                <td className={`t-right ${neg ? "is-neg" : "is-pos"}`}>{fmtEUR(saldo)}</td>
                                <td className="t-right">
                                  <button
                                    className="rcBtn rcBtn--ghost"
                                    type="button"
                                    onClick={() => openCategoryDetail(g.color, c)}
                                    disabled={(c.count || 0) === 0}
                                    title="Apri elenco movimenti"
                                    style={{ padding: "8px 10px", borderRadius: 12 }}
                                  >
                                    🔎 Vedi
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <div className="rcTableHint">💡 Le categorie a 0€ restano visibili ma “schiarite”.</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ✅ MODAL DETTAGLIO CATEGORIA */}
      {detail && (
        <div
          className="rcModalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Dettaglio categoria"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDetail();
          }}
        >
          <div className="rcModal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="rcModalHead">
              <div className="rcModalTitleRow">
                <span className="rcModalDot" style={{ backgroundColor: detail.color }} aria-hidden />
                <div>
                  <div className="rcModalTitle">
                    {detail.categoryLabel} <span style={{ opacity: 0.75 }}>· {detail.year}</span>
                  </div>
                  <div className="rcModalSub">
                    🔎 Filtra e scorri tutti i movimenti della categoria selezionata
                  </div>
                </div>
              </div>

              <button className="rcModalClose" type="button" onClick={closeDetail} aria-label="Chiudi">
                ✕
              </button>
            </div>

            <div className="rcModalTools">
              <input
                className="rcSearch"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca… (descrizione, fornitore, note)"
              />
              <label className="rcChk">
                <input
                  type="checkbox"
                  checked={onlyEntrate}
                  onChange={(e) => {
                    setOnlyEntrate(e.target.checked);
                    if (e.target.checked) setOnlyUscite(false);
                  }}
                />
                Solo entrate 💰
              </label>
              <label className="rcChk">
                <input
                  type="checkbox"
                  checked={onlyUscite}
                  onChange={(e) => {
                    setOnlyUscite(e.target.checked);
                    if (e.target.checked) setOnlyEntrate(false);
                  }}
                />
                Solo uscite 💸
              </label>

              <div className="rcModalTotals">
                <span>💰 {fmtEUR(detailTotals.entrate)}</span>
                <span>💸 {fmtEUR(detailTotals.uscite)}</span>
                <span>📈 {fmtEUR(detailTotals.saldo)}</span>
              </div>
            </div>

            <div className="rcModalBody">
              {filteredDetailTx.length === 0 ? (
                <div className="rcEmpty" style={{ boxShadow: "none" }}>
                  <div className="rcEmpty__title">Nessun movimento</div>
                  <div className="rcEmpty__text">Prova a togliere i filtri o a cambiare ricerca.</div>
                </div>
              ) : (
                <table className="rcTxTable">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descrizione</th>
                      <th>Tipo</th>
                      <th className="t-right">Importo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDetailTx.map((t) => {
                      const imp = parseAmount(t?.importo);
                      const neg = imp < 0;
                      return (
                        <tr key={t?.id || `${t?.data}-${t?.descrizione}-${t?.importo}`}>
                          <td style={{ whiteSpace: "nowrap" }}>{toDateLabelIt(t?.data)}</td>
                          <td>
                            <div style={{ fontWeight: 950 }}>{t?.descrizione || "—"}</div>
                            <div style={{ opacity: 0.75, fontSize: "0.86rem" }}>
                              {t?.fornitore ? `🏪 ${t.fornitore}` : ""} {t?.note ? ` · 📝 ${t.note}` : ""}
                            </div>
                          </td>
                          <td>{t?.tipo || (imp < 0 ? "Uscita" : "Entrata")}</td>
                          <td className={`t-right ${neg ? "is-neg" : "is-pos"}`}>{fmtEUR(imp)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="rcModalFoot">
              <button className="rcBtn rcBtn--ghost" type="button" onClick={closeDetail}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
